import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "../CustomFetchBase";

export const PricingApi = createApi({
  reducerPath: "PricingApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["Pricing", "PricingPlans", "ActivePricing"],
  endpoints: (builder) => ({
    // Get all pricing plans with pagination (Admin only)
    getAllPricingPlans: builder.query({
      query: ({
        page = 1,
        limit = 10,
        search = "",
        status = "",
        popular = "",
      } = {}) => ({
        url: `/pricing?page=${page}&limit=${limit}&search=${search}&status=${status}&popular=${popular}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["PricingPlans"],
    }),

    // Get active pricing plans (for public display)
    getActivePricingPlans: builder.query({
      query: () => ({
        url: "/pricing/active",
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["ActivePricing"],
    }),

    // Get pricing plan by ID
    getPricingById: builder.query({
      query: (id) => ({
        url: `/pricing/${id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: (result, error, id) => [{ type: "Pricing", id }],
    }),

    // Create new pricing plan (Admin only)
    createPricingPlan: builder.mutation({
      query: (data) => ({
        url: "/pricing",
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["PricingPlans", "ActivePricing"],
    }),

    // Update pricing plan (Admin only)
    updatePricingPlan: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/pricing/${id}`,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Pricing", id },
        "PricingPlans",
        "ActivePricing",
      ],
    }),

    // Edit pricing plan - partial update (Admin only)
    editPricingPlan: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/pricing/${id}`,
        method: "PATCH",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Pricing", id },
        "PricingPlans",
        "ActivePricing",
      ],
    }),

    // Delete pricing plan (Admin only)
    deletePricingPlan: builder.mutation({
      query: (id) => ({
        url: `/pricing/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["PricingPlans", "ActivePricing"],
    }),

    // Toggle active/inactive status (Admin only)
    togglePricingStatus: builder.mutation({
      query: (id) => ({
        url: `/pricing/${id}/toggle-status`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Pricing", id },
        "PricingPlans",
        "ActivePricing",
      ],
    }),

    // Toggle popular status (Admin only)
    togglePopularStatus: builder.mutation({
      query: (id) => ({
        url: `/pricing/${id}/toggle-popular`,
        method: "PATCH",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Pricing", id },
        "PricingPlans",
        "ActivePricing",
      ],
    }),
    createRazorPayOrder: builder.mutation({
      query: (data) => ({
        url: "/razorpay/create-order",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PricingPlans"],
    }),
    verifyRazorPayPayment: builder.mutation({
      query: (data) => ({
        url: "/razorpay/verify-payment",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PricingPlans"],
    }),
  }),
});

export const {
  useGetAllPricingPlansQuery,
  useGetActivePricingPlansQuery,
  useGetPricingByIdQuery,
  useCreatePricingPlanMutation,
  useUpdatePricingPlanMutation,
  useEditPricingPlanMutation,
  useDeletePricingPlanMutation,
  useTogglePricingStatusMutation,
  useTogglePopularStatusMutation,
  useLazyGetAllPricingPlansQuery,
  useLazyGetActivePricingPlansQuery,
  useLazyGetPricingByIdQuery,
  useCreateRazorPayOrderMutation,
  useVerifyRazorPayPaymentMutation,
} = PricingApi;
